import React, { useEffect, useRef } from 'react';

function extractYoutubeId(url: string): string | null {
  const m = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/
  );
  return m ? m[1] : null;
}

interface Props {
  url: string;
  title: string;
  autoplay?: boolean;
  startSeconds?: number;
  onProgress?: (seconds: number, duration: number) => void;
}

const YouTubeEmbed: React.FC<Props> = ({
  url,
  title,
  autoplay = false,
  startSeconds = 0,
  onProgress,
}) => {
  const ytId = extractYoutubeId(url);
  const progressRef = useRef(onProgress);
  progressRef.current = onProgress;

  // Listen for YouTube postMessage events (requires enablejsapi=1 in the URL)
  useEffect(() => {
    if (!ytId) return;
    const handler = (event: MessageEvent) => {
      // Only trust messages that actually come from the YouTube frame.
      if (event.origin !== 'https://www.youtube-nocookie.com' &&
          event.origin !== 'https://www.youtube.com') return;
      if (typeof event.data !== 'string') return;
      try {
        const data = JSON.parse(event.data);
        if (data.event === 'infoDelivery' && data.info) {
          const t = data.info.currentTime;
          const d = data.info.duration;
          if (typeof t === 'number' && typeof d === 'number' && d > 0) {
            progressRef.current?.(Math.floor(t), Math.floor(d));
          }
        }
      } catch {
        // not a YouTube message, ignore
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [ytId]);

  if (!ytId) {
    return (
      <div className="aspect-video bg-black w-full overflow-hidden flex items-center justify-center">
        <p className="text-white/60 text-sm">Video konnte nicht geladen werden.</p>
      </div>
    );
  }

  const params = new URLSearchParams({ rel: '0', playsinline: '1', enablejsapi: '1' });
  if (autoplay) params.set('autoplay', '1');
  if (startSeconds > 0) params.set('start', String(Math.floor(startSeconds)));

  return (
    <iframe
      // Erweiterter Datenschutzmodus: keine Cookies vor dem Abspielen
      src={`https://www.youtube-nocookie.com/embed/${ytId}?${params.toString()}`}
      title={title}
      className="aspect-video w-full block"
      style={{ border: 0 }}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
      allowFullScreen={true}
    />
  );
};

export default YouTubeEmbed;
